/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Field, Form, Formik } from "formik";
import "react-day-picker/dist/style.css";
import * as Yup from "yup";
import { Layout, Text } from "components";
import { Label, SelectField } from "@fitinsur/components/Form";
import Icon from "components/Icon";
import DialogComponent from "@fitinsur/components/Dialog";
import { FitinsurAPI, MessageCenter, CacheManager } from "utils";
import Colors from "constants/Colors";
import { GlobalAuthContext, GlobalLoaderContext } from "store/context";
import ListEnquiryButtons from 'components/ListEnquiryButtons';
import {
  Content,
  Circle,
  DialogCancel,
  DialogConfirm,
  DialogSelect,
  ButtonContainer,
  LoadContainer
} from "./styles";
import {
  useGetOperationNavigation,
  useCreateEndorsement
} from "services/OperationService";
import { useListPersonProduct } from "services/ProductService";
import { LoaderContext } from '@fitinsur/contexts';
import { useGetPolicies } from "services/PolicyService";
import NotesModalV2 from "@fitinsur/components/NotesModalV2";
import { ResponsiveTable, ListHeader, NotesModal } from '@fitinsur/components';
import {
  useGetQuery,
  useGetStatusOptions
} from '@fitinsur/services/QuestionnairesService';
import {
  useGetQuestionnaireAnswerAsync,
  useSaveQuestionnaireAnswerAsync
} from '@fitinsur/services/QuestionnaireAnswerService';
import {
  useCheckConsumption
} from '@fitinsur/services/CreditLimitService';

export default function PolicyPage({ permissions, ...props }) {
  const authContext = useContext(GlobalAuthContext);
  const region = authContext.region === 'en-GB' ? 'en' : authContext.region;
  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const cache = new CacheManager();
  const location = useLocation();
  const loading = useContext(GlobalLoaderContext);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const history = useHistory();
  const formik = useRef();
  const [sortBy, setSortBy] = useState({ key: null, direction: null });
  const [listData, setListData] = useState([]);
  const [showEndorsement, setShowEndorsement] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currentOperation, setCurrentOperation] = useState(null);
  const [operationNavigation, setOperationNavigation] = useState(null);
  const [headerStatus, setHeaderStatus] = useState([]);
  const [showEnquiryNotesModal, setShowEnquiryNotesModal] = useState(false);
  const [notesModalProps, setNotesModalProps] = useState(null);

  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    icon: "times-circle",
    args: null,
  });

  const [idOperationSelected, setIdOperationSelected] = useState(null);
  const [notesModal, setNotesModal] = useState(false);
  const [message, setMessage] = useState([null]);
  const [modalities, setModalities] = useState([]);
  const [brokerList, setBrokerList] = useState([]);
  const [optionsStatus, setOptionsStatus] = useState([]);
  const [paginationControls, setPaginationControls] = useState({
    "currentPage": 1,
    "numberMaxRecords": null,
    "total": null,
    "totalPages": null,
    "numberRecords": null
  });

  const initialFilterData = [
    { label: "APÓLICE", key: 'reference', type: 'text' },
    { label: "ENDOSSO", key: 'endorsementReferenceNumber', type: 'text' },
    { label: "Nº DA COTAÇÃO", key: 'idOperationPolicy', type: 'text' },
    { label: "DATA DE CRIAÇÃO", key: 'operationDate', type: 'date' },
    { label: "CORRETORA", key: 'sompoBroker', type: 'list' },
    { label: "PRODUTO", key: 'productName', type: 'list' },
    { label: "MODALIDADE", key: 'modality', type: 'list' },
    // { label: "TOMADOR", key: 'borrower', type: 'text' },
    { label: "SEGURADO", key: 'insured', type: 'text' },
    { label: "IS", key: 'orderIs', type: 'range' },
    { label: "DATA INÍCIO", key: 'inceptionDateSompo', type: 'date' },
    { label: "DATA EXPIRAÇÃO", key: 'expiryDateSompo', type: 'date' },
    { label: "SITUAÇÃO", key: 'endorsementStatus', type: 'list' },
  ];

  const endorsementsTypes = [
    {
      label: "Endosso de Cancelamento",
      value: { idDomain: 400 }
    },
    {
      label: "Endosso com Movimento (Aumento de IS)",
      value: { idDomain: 398, idAnswerOption: 9034 }
    },
    {
      label: "Endosso com Movimento (Redução de IS)",
      value: { idDomain: 398, idAnswerOption: 9037 }
    },
    {
      label: "Endosso com Movimento (Prorrogação de Vigência)",
      value: { idDomain: 398, idAnswerOption: 9035 }
    },
    {
      label: "Endosso com Movimento (Redução de Vigência)",
      value: { idDomain: 398, idAnswerOption: 9036 }
    },
    {
      label: "Endosso sem Movimento",
      value: { idDomain: 254 },
    },
  ];

  const {
    data: useGetOperationNavigationData,
    loading: useGetOperationNavigationLoading,
    error: useGetOperationNavigationError,
    status: useGetOperationNavigationStatus,
    request: useGetOperationNavigationRequest,
  } = useGetOperationNavigation(operationNavigation);

  const {
    data: useGetPoliciesData,
    loading: useGetPoliciesLoading,
    error: useGetPoliciesError,
    status: useGetPoliciesStatus,
    request: useGetPoliciesRequest,
  } = useGetPolicies();

  const {
    data: useCreateEndorsementData,
    loading: useCreateEndorsementLoading,
    error: useCreateEndorsementError,
    status: useCreateEndorsementStatus,
    request: useCreateEndorsementRequest,
  } = useCreateEndorsement(currentOperation);

  const {
    data: useListPersonProductData,
    loading: useListPersonProductLoading,
    error: useListPersonProductError,
    status: useListPersonProductStatus,
    request: useListPersonProductRequest,
  } = useListPersonProduct();

  const {
    request: getStatusOptionsRequest,
  } = useGetStatusOptions();

  const {
    request: useGetQueryRequest,
  } = useGetQuery();

  const {
    loading: useGetQuestionnaireAnswerAsyncLoading,
    request: useGetQuestionnaireAnswerAsyncRequest,
  } = useGetQuestionnaireAnswerAsync();

  const {
    loading: useSaveQuestionnaireAnswerAsyncLoading,
    request: useSaveQuestionnaireAnswerAsyncRequest,
  } = useSaveQuestionnaireAnswerAsync();

  const {
    loading: useCheckConsumptionLoading,
    request: useCheckConsumptionRequest,
  } = useCheckConsumption(currentOperation);

  const initialValues = {
    filter: {
      reference: "",
      policyNumber: "",
      client: "",
      product: "",
      applicationType: "",
      insurer: "",
      obligor: "",
      broker: "",
      status: "",
      endorsementStatus: "",
      is: "",
      inceptionDate: "",
      expiryDate: "",
      idAction: "",
    },
    endorsements: {},
  };

  const showEndorsementRow = (policy, state) => {
    if (state) {
      setShowEndorsement([...showEndorsement, policy.idOperation]);
    } else {
      setShowEndorsement(
        showEndorsement.filter(
          (idOperation) => idOperation != policy.idOperation
        )
      );
    }
  };

  const newEndorsement = (idOperation) => {
    setCurrentOperation(idOperation);

    setDialog({
      visible: true,
      title: "SELECIONE TIPO DE ENDOSSO",
      icon: "exclamation",
      args: null,
    });
  };

  const columns = [
    {
      title: "APÓLICE",
      value: "reference",
      sortKey: 'reference',
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return (
          <>
            <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
              <Circle>{![27].includes(listItem.idDomain) ? "E" : "A"}</Circle>
              <br />
              {listItem?.endorsements?.length >= 1 &&
                (showEndorsement.includes(listItem.idOperation) ? (
                  <Icon name={"arrowup"}
                    onClick={() =>
                      showEndorsementRow(listItem, false)
                    }
                  />
                ) : (
                  <Icon name={"arrowdown"}
                    onClick={() =>
                      showEndorsementRow(listItem, true)
                    }
                  />
                ))}{" "}
              {listItem.endorsementReferenceNumber || listItem.referenceNumber}
            </Label>
          </>
        );
      },
    },
    {
      title: "Nº DA COTAÇÃO",
      value: "idOperationRoot",
      sortKey: 'idOperationRoot',
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return (
          <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
            {listItem?.idDomain === 27 ? listItem?.idOperationRoot || listItem?.rootOperationId : listItem?.idQuotationOperation || listItem?.idOperation}
          </Label>
        );
      },
    },
    {
      title: "DATA DE CRIAÇÃO",
      value: "operationDate",
      sortKey: 'operationDate',
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return <>
          <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
            {new Date(listItem.operationDate).toLocaleDateString('pt-br')}
          </Label>
        </>
      },
    },
    {
      title: "CORRETORA",
      value: "sompoBroker",
      sortKey: 'broker',
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return (
          <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
            {listItem?.nmBroker || listItem?.sompoBroker}
          </Label>
        );
      },
    },
    {
      title: "PRODUTO",
      value: "applicationType",
      sortKey: 'applicationType',
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return (
          <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
            {listItem?.productName}
          </Label>
        );
      },
    },
    {
      title: "MODALIDADE",
      value: "modality",
      sortKey: 'modality',
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return (
          <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
            {listItem?.modality || listItem?.modalityPrivateSector}
          </Label>
        );
      },
    },
    // {
    //   title: "TOMADOR",
    //   value: "borrower",
    //   sortKey: 'borrower',
    //   minWidth: 80,
    //   color: (item) => handleColorLabel(item),
    //   component: (listItem) => {
    //     return (
    //       <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
    //         {listItem?.borrower}
    //       </Label>
    //     );
    //   },
    // },
    {
      title: "SEGURADO",
      value: "insured",
      sortKey: 'insured',
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return <>
          <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
            {listItem?.contactClientName ||  listItem?.insured}
          </Label>
        </>
      },
    },
    {
      title: "IS",
      value: "is",
      sortKey: 'orderIs',
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return <>
          <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
            {listItem?.operationMaxLimClaim}
          </Label>
        </>
      },
    },
    {
      title: "DATA INÍCIO",
      value: "inceptionDate",
      sortKey: 'inceptionDate',
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return <>
          <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
            {listItem.inceptionDate ?
              new Date(listItem.inceptionDate + "T03:00:00Z").toLocaleDateString('pt-br') :
              '-'
            }
          </Label>
        </>
      },
    },
    {
      title: "DATA EXPIRAÇÃO",
      value: "expiryDate",
      sortKey: 'expiryDate',
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return <>
          <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
            {listItem.expiryDate ?
              new Date(listItem.expiryDate + "T03:00:00Z").toLocaleDateString('pt-br') :
              '-'
            }
          </Label>
        </>
      },
    },
    {
      title: "SITUAÇÃO",
      value: "status",
      minWidth: 80,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return <>
          <Label wordWrap fontSize={13} color={handleColorLabel(listItem)}>
            {(([447, 448].includes(listItem.idDomainStatus) && listItem?.additionalInformation) || listItem.mediationFinished) &&
              <Icon
                name={'info'}
                title="Informações Adicionais"
                style={{ margin: 0, marginBottom: -5 }}
                className="mr-1"
                onClick={() => {
                  setIdOperationSelected(listItem.idOperation);
                  setNotesModal(true);
                }}
              />
            }
            {listItem['statusOperation'] || listItem['status'] || '-'}
          </Label>
        </>
      },
    },
    {
      title: "AÇÕES",
      value: "action",
      minWidth: 60,
      color: (item) => handleColorLabel(item),
      component: (listItem) => <ListEnquiryButtons
        editRoute={checkOperationRoute}
        listItem={listItem}
        permissions={permissions || {}}
        authContextToken={authContext?.token}
        isPolicyScreen
        newEndorsement={newEndorsement}
        toDocument={() => history.push(`/broking/document-management/${listItem.idDomain === 27 ? listItem.idOperationRoot : listItem.idOperation}`)}
      />
    },
  ];

  const storedFilterOption = localStorage.getItem('policy-filter-sompo');
  const storedStringFilterOption = localStorage.getItem('str-policy-filter-sompo');
  let quotesUrl = '/polices' + (storedStringFilterOption ? '?' + storedStringFilterOption : '');

  const [filterData, setFilterData] = useState(storedFilterOption ? JSON.parse(storedFilterOption) : initialFilterData);

  const handleColorLabel = (item) => {
    if (item.isRejected) return Colors.GRAY[3];
    else if (item.isApproved) return Colors.GRAY[0];
    else if ([447, 448].includes(item.idDomainStatus) && item?.additionalInformation) return Colors.RED;
    else if (item.mediationFinished) return Colors.TERTIARY;
    else return null;
  };

  useEffect(() => {
    updateIsLoadingStatus(
      useGetOperationNavigationLoading ||
      useGetPoliciesLoading ||
      useCreateEndorsementLoading ||
      useListPersonProductLoading ||
      useGetQuestionnaireAnswerAsyncLoading ||
      useSaveQuestionnaireAnswerAsyncLoading ||
      useCheckConsumptionLoading
    );
  }, [
    useGetOperationNavigationLoading,
    useGetPoliciesLoading,
    useCreateEndorsementLoading,
    useListPersonProductLoading,
    useGetQuestionnaireAnswerAsyncLoading,
    useSaveQuestionnaireAnswerAsyncLoading,
    useCheckConsumptionLoading
  ]);

  useEffect(() => {
    if (useGetOperationNavigationStatus === 200) {
      if (!useGetOperationNavigationData.value?.route) {
        history.push(`/edit-enquiry/${operationNavigation}`);
        setOperationNavigation(null);
      } else if (useGetOperationNavigationData.value?.route) {
        return history.push(useGetOperationNavigationData.value?.route);
      }
    }
  }, [useGetOperationNavigationStatus]);

  useEffect(() => {
    if (operationNavigation) useGetOperationNavigationRequest();
  }, [operationNavigation]);

  useEffect(() => {
    async function updateEndorsementNotPreAgreedType(idProduct, idOperation, idAnswerOption) {
      const [endorsementNotPreAgreedType] = await useGetQuestionnaireAnswerAsyncRequest(
        null,
        { idQuestions: [3530] },
        null,
        `/v2/operation/${idOperation}/questionnaire-answer`
      ).then(({ data: { value } }) => [
        ...value.persons,
        ...value.operations,
        ...value.payments,
        ...value.risks
      ]);

      const answerOption = endorsementNotPreAgreedType.answerOptions.find(
        opt => opt.idAnswerOption === idAnswerOption
      );

      endorsementNotPreAgreedType.answer = answerOption.dsAnswerOption;
      endorsementNotPreAgreedType.idAnswerOption = answerOption.idAnswerOption;

      await useSaveQuestionnaireAnswerAsyncRequest(
        [endorsementNotPreAgreedType],
        null,
        null,
        `/v2/operation/${idOperation}/questionnaire-answer`
      );

      history.push(`/edit-enquiry/${idProduct}/${idOperation}`);
    }

    if (useCreateEndorsementStatus) setCurrentOperation(null);

    if (useCreateEndorsementStatus === 200) {
      const { idProduct, idOperation } = useCreateEndorsementData.value;

      const endorsementType = dialog.args;
      if (!endorsementType?.idAnswerOption) {
        return history.push(`/edit-enquiry/${idProduct}/${idOperation}`);
      }

      updateEndorsementNotPreAgreedType(
        idProduct,
        idOperation,
        endorsementType.idAnswerOption
      );
    } else if (useCreateEndorsementStatus === 400) {
      useCreateEndorsementError.errors?.map(error => {
        if (error.message) mc.error(error.message)
      })
      return;
    }
  }, [useCreateEndorsementStatus]);

  useEffect(() => {
    if (useGetPoliciesStatus === 200) {
      if (useGetPoliciesData?.pagination_control?.currentPage == 1) {
        setListData(useGetPoliciesData.ret_value);
      } else {
        setListData([...listData, ...useGetPoliciesData.ret_value]);
      }

      setModalities(useGetPoliciesData.modalities);
      setHeaderStatus(useGetPoliciesData.statusList);
      setTimeout(() => updateIsLoadingStatus(false), 1000);

      let d = { ...useGetPoliciesData.pagination_control };
      d.currentPage = parseInt(d.currentPage);
      setPaginationControls(d);
      setCurrentPage(useGetPoliciesData?.pagination_control?.currentPage);
      setTotalPages(useGetPoliciesData?.pagination_control?.totalPages);
    }
  }, [useGetPoliciesStatus]);

  useEffect(() => {
    useGetPoliciesRequest(null, {
      loadLastDocument: true,
      loadOperationPersonRoles: true,
      page: currentPage,
      idQuestionInsured: 3373,
      idBrokerQuestion: 3386,
      idQuestionBorrower: 1519,
      idModalityQuestion: 1551,
      idModalityQuestionPrivate: 1552,
      idQuestionClientName: 3373,
      idQuestionClientEmail: 828,
      idQuestionClientPhone: 829,
      idQuestionApplicationType: 1032,
      idQuestionPolicyNumber: 1430,
      idQuestionInceptionDate: 3447,
      idQuestionExpiryDate: 3448,
      idQuestionEndorsementInceptionDate: 3495,
      idQuestionEndorsementExpiryDate: 3499,
      idsDomain: [26, 570],
      additionalInformationQst: 1812,
      ...(filterData
        .filter(v => v.value)
        .reduce((a, b) => {
          a[b.key] = b.value;
          return a
        }, {}))
    });

    updateIsLoadingStatus(false);

    fitinsur.setAuthToken(authContext.token);
    useListPersonProductRequest();
    RequestBrokerList();
    requestStatusOptions();

    cache
      .getMessage("NO_ITEMS_LIST", region, "355", authContext.token)
      .then((msg) => setMessage(msg))
      .catch((err) => console.log("error", err));
  }, []);

  useEffect(() => {
    if (location.search) {
    } else {
      const queryString =
        localStorage.getItem("str-management-filter-sompo") || "";
      const filterQueryString = `?${queryString}`;
    }
  }, []);

  useEffect(() => {
    if (!notesModal) setIdOperationSelected(null);
  }, [notesModal]);

  const handleSortList = (key) => {
    let direction = "asc";
    if (sortBy.key === key && sortBy.direction === "asc") {
      direction = "desc";
    }

    const sort = { key: key, direction: direction };
    setSortBy(sort);

    let e = paginationControls;
    e.currentPage = 1;
    setPaginationControls(e);

    useGetPoliciesRequest(null, {
      loadLastDocument: true,
      loadOperationPersonRoles: true,
      page: 1,
      idQuestionInsured: 3373,
      idBrokerQuestion: 3386,
      idQuestionBorrower: 1519,
      idModalityQuestion: 1551,
      idModalityQuestionPrivate: 1552,
      idQuestionClientName: 3373,
      idQuestionClientEmail: 828,
      idQuestionClientPhone: 829,
      idQuestionApplicationType: 1032,
      idQuestionPolicyNumber: 1430,
      idQuestionInceptionDate: 3447,
      idQuestionExpiryDate: 3448,
      additionalInformationQst: 1812,
      idQuestionEndorsementInceptionDate: 3495,
      idQuestionEndorsementExpiryDate: 3499,
      sortBy: JSON.stringify(sort),
      ...filterData
        .filter((v) => v.value)
        .reduce((a, b) => {
          a[b.key] = b.value;
          return a;
        }, {}),
    });
  };

  const handleFilter = (resetFilter) => {
    quotesUrl =
      "/polices" +
      (localStorage.getItem("str-policy-filter-sompo")
        ? "?" + localStorage.getItem("str-policy-filter-sompo")
        : "");
    let e = paginationControls;
    e.currentPage = 1;
    setPaginationControls(e);

    const refFilterData = resetFilter ? initialFilterData : filterData;

    useGetPoliciesRequest(null, {
      loadLastDocument: true,
      loadOperationPersonRoles: true,
      page: 1,
      idQuestionInsured: 3373,
      idBrokerQuestion: 3386,
      idQuestionBorrower: 1519,
      idModalityQuestion: 1551,
      idModalityQuestionPrivate: 1552,
      idQuestionClientName: 3373,
      idQuestionClientEmail: 828,
      idQuestionClientPhone: 829,
      idQuestionApplicationType: 1032,
      idQuestionPolicyNumber: 1430,
      idQuestionInceptionDate: 3447,
      idQuestionExpiryDate: 3448,
      additionalInformationQst: 1812,
      idQuestionEndorsementInceptionDate: 3495,
      idQuestionEndorsementExpiryDate: 3499,
      sortBy: JSON.stringify(sortBy),
      ...refFilterData
        .filter((v) => v.value)
        .reduce((a, b) => {
          a[b.key] = b.value;
          return a;
        }, {}),
    });

    if (resetFilter) {
      localStorage.removeItem("str-policy-filter-sompo");
      localStorage.removeItem("policy-filter-sompo");
      history.push(`/polices`);
    } else {
      history.push(`${quotesUrl}`);
    }
  };

  const exportList = async () => {
    const exportName = "texel-export-list-report";
    let exportList = sessionStorage.getItem(exportName);
    let list = [];
    if (exportList) list = JSON.parse(sessionStorage.getItem(exportName));
    let verify =
      list.length > 0
        ? list.filter((item) => item.nameList == "Apolices Report")
        : false;
    if (!verify || verify.length == 0) {
      updateIsLoadingStatus(true);
      setTimeout(() => updateIsLoadingStatus(false), 1000);

      const params = {
        idQuestionInsured: 3373,
        idBrokerQuestion: 3386,
        idQuestionBorrower: 1519,
        idModalityQuestion: 1551,
        idModalityQuestionPrivate: 1552,
        idQuestionClientName: 3373,
        idQuestionClientEmail: 828,
        idQuestionClientPhone: 829,
        idQuestionApplicationType: 1032,
        idQuestionPolicyNumber: 1430,
        idQuestionInceptionDate: 3447,
        idQuestionExpiryDate: 3448,
        idQuestionEndorsementInceptionDate: 3495,
        idQuestionEndorsementExpiryDate: 3499,
        control: {
          currentPage: 1,
        },
        brokerColumn: true,
        extraColumns: "sompoPolicy",
        ...filterData
          .filter((v) => v.value)
          .reduce((a, b) => {
            a[b.key] = b.value;
            return a;
          }, {}),
      };

      const urlParams = new URLSearchParams(params).toString();

      const url = `?${urlParams}`;

      fitinsur.setAuthToken(authContext.token);
      fitinsur.setAuthToken(authContext.token);
      const document = await fitinsur
        .exportEndorsementsSompo(url)
        .then((x) => x.json());
      updateIsLoadingStatus(true);
      setTimeout(() => updateIsLoadingStatus(false), 1000);
      if (document.idDocument) {
        list.push({
          idDocument: document.idDocument,
          processing: true,
          nameList: "Relatório de apólices",
        });
        sessionStorage.setItem(exportName, JSON.stringify(list));
      }
    } else {
      mc.error(
        "Por favor, aguarde o donwload do relatório de crédito ser finalizado."
      );
    }
  };

  const RequestBrokerList = async () => {
    const { data } = await useGetQueryRequest({
      id: "fitQueryPersonBroker",
      params: {
        id: "fitQueryPersonBroker",
        queryTail: true,
        personName: "",
      },
    });

    try {
      if (data.ret_code !== 200) return;

      const persons = data.ret_value.map((person) => ({
        ...person,
        label: person.personName,
        value: person.id,
      }));

      setBrokerList(persons);
    } catch (err) {
      console.log("err fitQueryPersonBroker", err);
    }
  };

  const checkOperationRoute = (idOperation, idProduct, idDomainStatusDoc) => {
    updateIsLoadingStatus(true);
    fitinsur.setAuthToken(authContext.token);
    fitinsur
      .requestPolicyNavigation(idOperation)
      .then((x) => x.json())
      .then((res) => {
        if (res.ret_code !== 200) {
          mc.error(res.ret_msg_details);
          return;
        }

        let route =
          res.ret_value.route || `/edit-enquiry/${idProduct}/${idOperation}`;
        if (route) {
          updateIsLoadingStatus(false);
          history.push({ pathname: route, state: res.ret_value.idNavigation == 99 ? { disabled: true, idProduct: idProduct } : { status: idDomainStatusDoc, idProduct: idProduct } });
        } else {
          mc.error("NAVIGATION NOT DEFINED");
          updateIsLoadingStatus(false);
        }
      })
      .catch(() => updateIsLoadingStatus(false));
  };

  const requestStatusOptions = async () => {
    const { data } = await getStatusOptionsRequest(null, { idDomains: [25, 400, 398, 254] });

    const formattedStatus = data.value.map((status) => ({
      ...status,
      label: status.statusDescription,
      value: status.idDomainStatusList?.join(",") || status.idDomainStatus,
    }));

    setOptionsStatus(formattedStatus);
  };

  async function updateData(item, requireAttention) {
    setListData(
      listData.map((enquiry) => {
        if (item.idOperation == enquiry.idOperation) {
          enquiry.requireAttention = requireAttention;
        }
        return enquiry;
      })
    );
  }

  async function handleGetNotes(item) {
    fitinsur.setAuthToken(authContext.token);
    fitinsur
      .requestOperationNotes(item.idOperation)
      .then((response) => response.json())
      .then((data) => {
        setNotesModalProps({
          item,
          notes: data.ret_value.notes,
          requireAttention: data.ret_value.requireAttention,
        });
      })
      .catch((error) => {
        mc.error("Houve um erro enquanto buscamos seus dados.");
        console.error("fitPostSummary - Error:", error);
      });
  }

  const handleSubmitNotes = (item, notes, isRequireAttention) => {
    loading.start();
    fitinsur.setAuthToken(authContext.token);
    fitinsur
      .requestOperationNotesUpdateV2(
        item.idOperation,
        notes,
        isRequireAttention
      )
      .then((response) => response.json())
      .then((data) => {
        if (data.ret_code !== 200) {
          mc.error(data.ret_msg);
          loading.stop();
          return;
        }
        handleGetNotes(item);
        loading.stop();
      })
      .catch(() => {
        loading.stop();
      });
    // loading.stop();
  };

  function setNextPage(paginationControls) {
    let e = paginationControls;
    e.currentPage++;
    setPaginationControls(e);

    useGetPoliciesRequest(null, {
      loadLastDocument: true,
      loadOperationPersonRoles: true,
      page: e.currentPage,
      idQuestionInsured: 3373,
      idBrokerQuestion: 3386,
      idQuestionBorrower: 1519,
      idModalityQuestion: 1551,
      idModalityQuestionPrivate: 1552,
      idQuestionClientName: 3373,
      idQuestionClientEmail: 828,
      idQuestionClientPhone: 829,
      idQuestionApplicationType: 1032,
      idQuestionPolicyNumber: 1430,
      idQuestionInceptionDate: 3447,
      idQuestionExpiryDate: 3448,
      additionalInformationQst: 1812,
      idQuestionEndorsementInceptionDate: 3495,
      idQuestionEndorsementExpiryDate: 3499,
      sortBy: JSON.stringify(sortBy),
      ...filterData
        .filter((v) => v.value)
        .reduce((a, b) => {
          a[b.key] = b.value;
          return a;
        }, {}),
    });
  }

  const handleCheckConsumption = async (isIssue = false) => {
    try {
      const { data: consumptionData } = await useCheckConsumptionRequest(
        {}, 
        {
          isIssue,
          validateOnlyGlobalFinanceBalanceStatus: true
        }
      );
      return !!consumptionData?.value?.availableLimit;
    } catch (err) {
      if (err?.response?.data?.errors?.length) {
        err?.response?.data?.errors.map(error => mc.error(error.message))
      } else {
        mc.error("Falha ao confirmar a seu endosso, tente novamente!");
      }
      return false;
    }
  }

  return (
    <Layout title={"Apólices e Endossos"}>
      <Content>
        <DialogComponent
          size="6x"
          icon={dialog.icon}
          title={dialog.title}
          show={dialog.visible}
        >
          <Formik
            initialValues={{ endorsementType: {} }}
            validationSchema={Yup.object().shape({
              endorsementType: Yup.string().required("* Campo obrigatório"),
            })}
            onSubmit={async (value) => {
              setDialog({ ...dialog, args: value.endorsementType });
              useCreateEndorsementRequest({ domain: value.endorsementType.idDomain });
            }}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <DialogSelect>
                  <Field
                    name="endorsementType"
                    value={values.endorsementType}
                    placeholder="Selecione"
                    component={SelectField}
                    options={endorsementsTypes}
                    onChange={(value) => setFieldValue("endorsementType", value)}
                  />
                </DialogSelect>
                <DialogCancel
                  type="button"
                  onClick={() => {
                    setDialog({
                      visible: false,
                      title: null,
                      icon: null,
                      args: null,
                    });
                  }}
                >
                  Cancelar
                </DialogCancel>
                <DialogConfirm type="submit">Confirmar</DialogConfirm>
              </Form>
            )}
          </Formik>
        </DialogComponent>

        <NotesModalV2
          show={showEnquiryNotesModal}
          handleClose={() => setShowEnquiryNotesModal(false)}
          onSubmit={(notes) =>
            handleSubmitNotes(notesModalProps?.item, notes, null)
          }
          notesHistory={notesModalProps?.notes}
          isRequireAttentionHistory={notesModalProps?.requireAttention}
          label={"Notas da Apólice"}
          notesModalProps={notesModalProps}
          handleGetNotes={handleGetNotes}
          updateData={updateData}
          handleSubmitNotes={handleSubmitNotes}
        />

        <NotesModal
          title={'Informações Adicionais'}
          textButtonConfirm={'Confirmar'}
          textButtonCancel={'Cancelar'}
          show={notesModal}
          setModalShow={setNotesModal}
          customCancelButton={[Colors.WHITE, Colors.PRIMARY1]}
          idOperation={idOperationSelected}
          idQuestion={1812}
          viewMode={true}
        />

        <Formik
          initialValues={initialValues}
          onSubmit={(values) => handleFilter(values)}
          innerRef={formik}
        >
          {(formikProps) => (
            <Form>
              <ListHeader
                idDomainList={319}
                idApplication={675}
                filterData={filterData}
                setFilterData={setFilterData}
                onHandleFilter={(resetFilter) => handleFilter(resetFilter)}
                listNameSpace={"policy-filter-sompo"}
                listUrl="/polices"
                optionsProduct={
                  useListPersonProductData?.ret_value
                    ? useListPersonProductData.ret_value.map((product) => ({
                      label: product.PRODUCT_NAME,
                      value: product.ID_PRODUCT,
                    }))
                    : []
                }
                optionsModalities={modalities}
                optionsBroker={brokerList}
                optionsStatus={optionsStatus}
                exportList={() => exportList(location.search || undefined)}
                availabelStatuses={[382, 383]}
              />
              <>
                <ResponsiveTable
                  onSortTable={handleSortList}
                  formikRef={formik?.current}
                  columns={columns}
                  data={listData}
                  showEndorsement={showEndorsement}
                />
                {!listData.length && (
                  <ButtonContainer style={{ minHeight: "450px" }}>
                    <Text
                      style={{
                        fontWeight: "bold",
                        color: Colors.GRAY[1],
                        margin: "auto",
                        cursor: "pointer",
                        marginTop: "10px",
                      }}
                      onClick={() => { }}
                    >
                      {message || ""}
                    </Text>
                  </ButtonContainer>
                )}
              </>
            </Form>
          )}
        </Formik>

        <LoadContainer>
          {!(
            !listData ||
            listData.length === 0 ||
            currentPage == totalPages
          ) ? (
            <ButtonContainer>
              <Text
                primary
                type="button"
                id="btnSubmit"
                style={{
                  fontWeight: "bold",
                  color: Colors.GRAY[1],
                  margin: "auto",
                  cursor: "pointer",
                  marginTop: "10px",
                }}
                onClick={() => setNextPage(paginationControls)}
              >
                +CARREGAR MAIS REGISTROS
              </Text>
            </ButtonContainer>
          ) : (
            <ButtonContainer>
              <Text
                primary
                type="button"
                id="btnSubmit"
                style={{
                  fontWeight: "bold",
                  color: Colors.GRAY[1],
                  margin: "auto",
                  cursor: "pointer",
                  marginTop: "10px",
                }}
                onClick={(e) => e.preventDefault()}
              >
                NÃO HÁ MAIS ITENS
              </Text>
            </ButtonContainer>
          )}
        </LoadContainer>
      </Content>
    </Layout>
  );
}
